import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, Info } from 'lucide-react';
import { billingAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { formatCurrency, formatDate } from '../../lib/utils';

/**
 * What this firm pays: active clients x price per client, per month.
 *
 * This page used to render three tiers — 299 / 799 / 1499 DKK with "Up to 50 documents/month" and
 * company caps — served from a `SUBSCRIPTION_PLANS` list in the backend. None of it was true. The
 * landing page the accountant signed up from says "one price per client — no transaction caps, no
 * tiers, no surprises", and the business actually charges 69-99 kr per client. An accountant who
 * read both saw two different products.
 *
 * There is nothing to choose here now, so there is no plan grid and no subscribe button: the price
 * is one number, the count is their own active clients, and the arithmetic comes from the same
 * `pricing` module as the invoice the admin raises.
 */
export default function BillingPage() {
    const [subscription, setSubscription] = useState(null);
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await billingAPI.getCurrentSubscription();
                setSubscription(data.subscription);
                setBilling(data.billing);
            } catch (err) {
                console.error('Failed to fetch billing data:', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="p-8">
                <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const kr = (v) => formatCurrency(Number(v || 0), billing?.currency || 'DKK');

    return (
        <div className="p-8 space-y-6 max-w-3xl" data-testid="billing-page">
            <div>
                <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
                    <CreditCard className="h-6 w-6 text-primary" /> Billing
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    One price per client, per month. No document caps, no tiers.
                </p>
            </div>

            {billing && (
                <Card data-testid="billing-summary">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                            {billing.firm_name || 'Your firm'}
                        </CardTitle>
                        <CardDescription>
                            {billing.client_count} active client{billing.client_count === 1 ? '' : 's'}
                            {' × '}{kr(billing.price_per_client)} per client
                            {billing.negotiated
                                ? <Badge variant="outline" className="ml-2 text-xs">agreed rate</Badge>
                                : <Badge variant="outline" className="ml-2 text-xs">standard rate</Badge>}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <dl className="space-y-1 font-mono text-sm">
                            <div className="flex justify-between">
                                <dt className="text-muted-foreground">
                                    {billing.client_count} × {kr(billing.price_per_client)}
                                </dt>
                                <dd data-testid="billing-ex-vat">{kr(billing.ex_vat)}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-muted-foreground">
                                    VAT ({Math.round((billing.vat_rate || 0) * 100)}%)
                                </dt>
                                <dd data-testid="billing-vat">{kr(billing.vat_amount)}</dd>
                            </div>
                            <div className="flex justify-between border-t border-border pt-1 font-bold">
                                <dt>Per month</dt>
                                <dd data-testid="billing-incl-vat">{kr(billing.incl_vat)}</dd>
                            </div>
                        </dl>
                        {billing.client_count === 0 && (
                            <p className="flex items-start gap-1.5 text-xs text-muted-foreground pt-2">
                                <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                                You have no active clients yet, so there is nothing to bill.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            {subscription && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Account access</CardTitle>
                        <CardDescription>
                            Active since {formatDate(subscription.current_period_start)}
                            {' · '}
                            {subscription.usage?.companies ?? 0} compan
                            {(subscription.usage?.companies ?? 0) === 1 ? 'y' : 'ies'}
                            {' · '}
                            {subscription.usage?.documents_this_month ?? 0} document
                            {(subscription.usage?.documents_this_month ?? 0) === 1 ? '' : 's'} this month
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-xs text-muted-foreground">
                            Your access is active. Usage is shown for information only — there are no
                            limits on documents or companies.
                        </p>
                    </CardContent>
                </Card>
            )}

            <p className="text-xs text-muted-foreground">
                Questions about your rate? Contact us — pricing is agreed per firm.
            </p>
        </div>
    );
}
